import React, { useContext, useEffect } from 'react';
import { NextPageContext } from 'next';
import QuestionsWithCategories from 'views/QuestionsWithCategories/QuestionsWithCategories';
import styles from 'pages/categories/index.module.css';
import { appAuthContext } from 'context/AuthContext';
import clickStreamPageview from 'utils/clickstream/useClickStreamPageview';
import { getLayout } from 'components/Layout/Layout';
import { Subjects } from 'views/QuestionsWithCategories/QuestionsWithCategories.data';
import getCategoriesData from 'views/QuestionsWithCategories/api/getCategoriesData';
import { QuestionApiResponse } from 'types/Questions.types';
import getQuerySSR from 'utils/getQuerySSR';
import { BannersType } from 'types/BannersType';
import CategoriesPanel from 'views/QuestionsWithCategories/components/CategoriesPanel';
import JsonLd from 'components/JsonLd/JsonLd';
import { buildItemListJsonLd } from 'utils/seo/jsonld';

type CategoriesPageProps = {
  subject: {
    name: string;
    imageName: string;
    link: string;
  };
  data: QuestionApiResponse | null;
  page: number;
  banners: BannersType;
  title: string;
  description: string;
};

const CategoriesPage = ({ subject, data, page }: CategoriesPageProps) => {
  const { user } = useContext(appAuthContext);

  // Проверить неавторизованного
  useEffect(() => {
    clickStreamPageview({
      id: user?.data?.attributes?.foreign_id,
      userType: user?.data?.attributes?.type,
    });
  }, []);

  return (
    <div className={styles.wrapper}>
      <CategoriesPanel subject={subject} />
      <QuestionsWithCategories data={data} page={page} />
      {data?.questions && data.questions.length > 0 && (
        <JsonLd id="categoriesItemList" data={buildItemListJsonLd(data.questions)} />
      )}
    </div>
  );
};

CategoriesPage.getLayout = getLayout;

export async function getServerSideProps(params: NextPageContext) {
  const slug = getQuerySSR(params.query?.slug);
  const page = getQuerySSR(params.query?.page);
  const klass = getQuerySSR(params.query?.klass);

  // Определяем категорию и возвращаем нужный title и description для шаблона
  const category =
    slug !== undefined
      ? Subjects.find((i) => i.link === slug)
      : slug === undefined
      ? Subjects[0]
      : null;

  if (!category) {
    return {
      redirect: {
        permanent: false,
        destination: '/404',
      },
      props: {},
    };
  }

  const isDefaultSubject = slug === undefined;
  const categoryName = category.name.replace(/[-]/g, '');
  const gradeTitle = klass ? `${klass} класс ` : '';

  const title = isDefaultSubject
    ? `Вопросы и ответы онлайн ${gradeTitle}| Учи.Ответы ${
        page && +page > 1 ? `, cтраница ${page}` : ''
      }`
    : `${categoryName} ${gradeTitle}- вопросы и ответы онлайн | Учи.Ответы${
        page && +page > 1 ? `, cтраница ${page}` : ''
      }`;

  const description = isDefaultSubject
    ? `Задай вопрос онлайн и получи ответ на Учи.Ответы${
        page && +page > 1 ? `, cтраница ${page}` : ''
      }`
    : `${categoryName}: задай вопрос онлайн и получи ответ на Учи.Ответы${
        page && +page > 1 ? `, cтраница ${page}` : ''
      }`;
  ///

  const data = await getCategoriesData(params, slug, page, klass, undefined, undefined);

  if (!data) {
    return {
      redirect: {
        permanent: false,
        destination: '/500',
      },
      props: {},
    };
  }

  const bannerObj = {
    bgColor: '#ABD8FF',
    desktop: {
      link: 'https://uchi.ru/profile/students?utm_source=otvety&utm_content=banner_side_categories',
      imageUrl: 'https://static.uchi.ru/files/otvety/banners/banner-categories29.06.png',
      eventInfo: 'banner_side_categories',
    },
    mobile: {
      link: 'https://uchi.ru/profile/students?utm_source=otvety&utm_content=banner_side_categories',
      imageUrl:
        'https://static.uchi.ru/files/otvety/banners/mobile-banner-categories29.06.png',
      eventInfo: 'banner_side_categories',
    },
  };

  return {
    props: {
      subject: category,
      data: data || null,
      page: page || 1,
      banners: bannerObj,
      title,
      description,
    },
  };
}

export default CategoriesPage;
